/**
 * B549 — group bookings, leg (1): the inquiry door.
 *
 * "More than 20 is a group booking; the groups team will book those"
 * (Pathik 2026-09-24). The buyer surfaces stop the stepper at the
 * channel's `groupThreshold` and offer the interrupt; this operation is
 * where the interrupt's form lands — organization, contact, preferred
 * date, headcount, kind — as a row the groups desk will read. A guest
 * session reaches it (the interrupt lives in the booking sheet, where a
 * guest token already exists), and STAFF may type one in from a call.
 *
 * Deliberately a WRITE op, not a public door: the clientKey makes a
 * double-pressed SEND one inquiry (Inv 4), and the guest token keeps the
 * public-door abuse surface unchanged.
 */
import { z } from "zod";
import { publicOp, readOp, writeOp, zEpochMs, zId } from "./common.js";

export const GROUP_INQUIRY_KINDS = ["school", "corporate", "tour", "other"] as const;

/** B549 (4a): the desk's stages — the queue's pills. GROWS, never renames. */
export const GROUP_STAGES = ["NEW", "QUOTED", "TENTATIVE", "CONFIRMED", "VOID"] as const;
export const GROUP_TICKETING = ["PER_SEAT", "GROUP_TICKET"] as const;
export const GROUP_QUOTE_COLLECT = ["HOSTED_INVOICE", "PAY_PAGE", "BANK_TRANSFER"] as const;
/** B549 (5): the invoice schedule's kinds, how recorded money arrived, and the derived state. */
export const GROUP_INVOICE_KINDS = ["DEPOSIT", "BALANCE", "FULL"] as const;
/** How the desk records money that arrived off-platform (group.payment.record's input). */
export const GROUP_DESK_PAYMENT_METHODS = ["BANK_TRANSFER", "CHEQUE", "CASH", "CARD_ELSEWHERE"] as const;
/** Every recorded method — B549 (6b) adds ONLINE: collected on the provider's payment link, recorded from its signed event. */
export const GROUP_PAYMENT_METHODS = [...GROUP_DESK_PAYMENT_METHODS, "ONLINE"] as const;
export const GROUP_INVOICE_STATES = ["OPEN", "PARTIALLY_PAID", "PAID", "OVERDUE", "VOID"] as const;

const zDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const zCount = z.number().int().min(0);
const zMinor = z.number().int().min(0);

/** Ruled (c): the desk's own deposit terms — never a platform default. */
const zDeposit = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("NONE") }).strict(),
  z.object({ kind: z.literal("PERCENT"), percent: z.number().int().min(1).max(100) }).strict(),
  z.object({ kind: z.literal("AMOUNT"), amountMinor: z.number().int().min(1) }).strict(),
]);
const zBalance = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("ON_ACCEPTANCE") }).strict(),
  z.object({ kind: z.literal("DAYS_BEFORE_VISIT"), days: z.number().int().min(0).max(365) }).strict(),
  z.object({ kind: z.literal("NET_AFTER_VISIT"), days: z.number().int().min(0).max(365) }).strict(),
]);
const zTerms = z
  .object({
    deposit: zDeposit,
    depositDueAt: zEpochMs.optional(),
    balance: zBalance,
    expiresAt: zEpochMs,
    collectVia: z.enum(GROUP_QUOTE_COLLECT),
  })
  .strict();

/** A quote line as the desk names it: WHAT and HOW MANY — never a price. */
const zQuoteLineIn = z.object({ productId: zId, variantId: zId, qty: z.number().int().min(1).max(100_000) }).strict();

const zQuoteLine = z
  .object({
    productId: zId,
    variantId: zId,
    productName: z.string().min(1),
    variantName: z.string().min(1),
    qty: z.number().int().min(1),
    groupUnitMinor: zMinor,
    publicUnitMinor: zMinor.optional(),
  })
  .strict();

const zQuote = z
  .object({
    status: z.enum(["DRAFT", "SENT"]),
    channelId: zId,
    slotId: zId,
    poolId: zId,
    visitAt: zEpochMs,
    ticketing: z.enum(GROUP_TICKETING),
    headcount: z.number().int().min(1),
    chaperones: zCount,
    currency: z.string().min(3).max(3),
    lines: z.array(zQuoteLine),
    seats: z.number().int().min(1),
    totalMinor: zMinor,
    publicTotalMinor: zMinor.optional(),
    terms: zTerms,
    depositMinor: zMinor,
    balanceDueAt: zEpochMs.optional(),
    allocationId: zId.optional(),
    heldSeats: zCount.optional(),
    sentAt: zEpochMs.optional(),
    savedAt: zEpochMs,
  })
  .strict();

/** The queue row's glance at the quote: VISIT and VALUE columns (438:2). */
const zQuoteGlance = z
  .object({
    status: z.enum(["DRAFT", "SENT"]),
    visitAt: zEpochMs,
    seats: z.number().int().min(1),
    totalMinor: zMinor,
    currency: z.string().min(3).max(3),
    /** Seats the quote's carve-out holds, once sent — the KPI's "seats held". */
    heldSeats: zCount.optional(),
    /** The option date: held seats return to sale here. */
    expiresAt: zEpochMs,
  })
  .strict();

export const groupInquiry = writeOp({
  name: "group.inquiry",
  permission: "order:write",
  principals: ["CUSTOMER", "STAFF"],
  fields: {
    channelId: zId,
    organisation: z.string().min(1).max(200),
    contactName: z.string().min(1).max(120),
    contactEmail: z.string().email().max(254),
    contactPhone: z.string().min(3).max(40).optional(),
    /** The day they have in mind, if any — a calendar date, not a slot. */
    preferredDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    headcount: z.number().int().min(1).max(100_000),
    kind: z.enum(GROUP_INQUIRY_KINDS),
    notes: z.string().max(2000).optional(),
  },
  response: z.object({ inquiryId: zId, receivedAt: z.number().int() }).strict(),
  exampleRequest: {
    channelId: "web",
    organisation: "Lakeview Elementary",
    contactName: "Dana Field",
    contactEmail: "dana@lakeview.example",
    headcount: 25,
    kind: "school",
  },
  exampleResponse: { inquiryId: "gi-1", receivedAt: 1_726_000_000_000 },
});

/**
 * B549 leg (3) — the desk's queue read: every inquiry of the tenant,
 * newest first. MANAGER/ADMIN back office (the Bookings audience); the
 * lifecycle beyond NEW (quotes, tentative holds, invoices) is its own
 * leg, so the row's status vocabulary is deliberately closed at NEW here
 * and will GROW, never rename.
 */
export const groupInquiryList = readOp({
  name: "group.inquiry.list",
  permission: "order:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: {},
  response: z
    .object({
      inquiries: z.array(
        z
          .object({
            inquiryId: zId,
            channelId: zId,
            organisation: z.string().min(1),
            contactName: z.string().min(1),
            contactEmail: z.string().min(1),
            contactPhone: z.string().min(1).optional(),
            preferredDate: z
              .string()
              .regex(/^\d{4}-\d{2}-\d{2}$/)
              .optional(),
            headcount: z.number().int().min(1),
            kind: z.enum(GROUP_INQUIRY_KINDS),
            notes: z.string().optional(),
            // B549 (4a): the vocabulary grew past NEW — additive, the leg-3 promise
            status: z.enum(GROUP_STAGES),
            createdAt: z.number().int(),
            quote: zQuoteGlance.optional(),
          })
          .strict(),
      ),
    })
    .strict(),
  exampleRequest: {},
  exampleResponse: {
    inquiries: [
      {
        inquiryId: "gi-1",
        channelId: "web",
        organisation: "Lakeview Elementary",
        contactName: "Dana Field",
        contactEmail: "dana@lakeview.example",
        headcount: 25,
        kind: "school",
        status: "NEW",
        createdAt: 1_726_000_000_000,
      },
    ],
  },
});

const zPayment = z
  .object({
    paymentId: zId,
    amountMinor: z.number().int().min(1),
    method: z.enum(GROUP_PAYMENT_METHODS),
    reference: z.string().min(1).optional(),
    receivedAt: zEpochMs,
    recordedAt: zEpochMs,
    /** B549 (6): recorded on an already-CONFIRMED booking — posted against the receivable, not the deposit liability. */
    settlesReceivable: z.boolean().optional(),
    /** B549 (6b): the provider event an ONLINE payment was recorded from. */
    providerEventId: z.string().min(1).optional(),
  })
  .strict();

/** B549 (6b): the provider's payment link on an invoice — the hosted page the organizer pays on. */
const zCollection = z
  .object({
    provider: z.string().min(1),
    providerInvoiceId: z.string().min(1),
    providerAccountId: z.string().min(1).optional(),
    hostedUrl: z.string().url(),
    amountMinor: zMinor,
    createdAt: zEpochMs,
    voidedAt: zEpochMs.optional(),
  })
  .strict();

/**
 * B549 (5): an invoice as the desk reads it — the platform record plus its
 * DERIVED state and paid total (the server's clock decides OVERDUE, so two
 * screens can never disagree about it).
 */
const zInvoice = z
  .object({
    number: z.string().min(1),
    kind: z.enum(GROUP_INVOICE_KINDS),
    amountMinor: zMinor,
    currency: z.string().min(3).max(3),
    issuedAt: zEpochMs,
    dueAt: zEpochMs,
    afterVisit: z.boolean(),
    payments: z.array(zPayment),
    voidedAt: zEpochMs.optional(),
    collection: zCollection.optional(),
    state: z.enum(GROUP_INVOICE_STATES),
    paidMinor: zMinor,
  })
  .strict();

/** B561: what the desk mails the organizer. GROWS, never renames. */
export const GROUP_MAIL_KINDS = ["QUOTE", "INVOICE", "TICKETS", "REMINDER"] as const;

const zMailed = z
  .object({ kind: z.enum(GROUP_MAIL_KINDS), at: zEpochMs, to: z.string().min(1), invoiceNumber: z.string().min(1).optional() })
  .strict();

const zInquiryFull = z
  .object({
    inquiryId: zId,
    channelId: zId,
    organisation: z.string().min(1),
    contactName: z.string().min(1),
    contactEmail: z.string().min(1),
    contactPhone: z.string().min(1).optional(),
    preferredDate: zDate.optional(),
    headcount: z.number().int().min(1),
    kind: z.enum(GROUP_INQUIRY_KINDS),
    notes: z.string().optional(),
    status: z.enum(GROUP_STAGES),
    createdAt: z.number().int(),
    revision: zCount,
    quote: zQuote.optional(),
    // B549 (5): the booking after the quote
    acceptedAt: zEpochMs.optional(),
    bookingRef: z.string().min(1).optional(),
    invoices: z.array(zInvoice).optional(),
    voidedAt: zEpochMs.optional(),
    /** B549 (6): the on_account order minted when the booking CONFIRMED. */
    orderId: zId.optional(),
    /** B561: every mail the desk sent the organizer that landed, oldest first. */
    mailed: z.array(zMailed).optional(),
  })
  .strict();

const EXAMPLE_QUOTE = {
  status: "DRAFT" as const,
  channelId: "groups",
  slotId: "harbor#2026-10-14T10:30",
  poolId: "pool-main",
  visitAt: 1_792_000_000_000,
  ticketing: "PER_SEAT" as const,
  headcount: 54,
  chaperones: 6,
  currency: "USD",
  lines: [{ productId: "harbor", variantId: "student", productName: "Harbor cruise", variantName: "Student", qty: 60, groupUnitMinor: 1850, publicUnitMinor: 2400 }],
  seats: 60,
  totalMinor: 111_000,
  publicTotalMinor: 144_000,
  terms: { deposit: { kind: "PERCENT" as const, percent: 25 }, balance: { kind: "DAYS_BEFORE_VISIT" as const, days: 14 }, expiresAt: 1_791_000_000_000, collectVia: "HOSTED_INVOICE" as const },
  depositMinor: 27_750,
  balanceDueAt: 1_790_790_400_000,
  savedAt: 1_790_000_000_000,
};

/**
 * B549 (4a) — one inquiry with its quote, for the quote builder (441:2) and
 * the booking detail (443:2). `pool` is the allocation card's figures for
 * the quote's pool (absent before a quote names one): capacity, sold, held,
 * and the seats OTHER live quotes hold there. Absent inquiry = 404 (a
 * foreign tenant's id answers exactly like a missing one, Inv 10).
 */
export const groupInquiryGet = readOp({
  name: "group.inquiry.get",
  permission: "order:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { inquiryId: zId },
  response: z
    .object({
      inquiry: zInquiryFull,
      pool: z.object({ capacity: zCount, sold: zCount, held: zCount, heldByOtherQuotes: zCount }).strict().optional(),
      /**
       * B549 (5): the booking's carve-out as it stands — the seats it holds
       * and until when (the release-back sweep returns them then). Absent
       * before a quote was sent; `active: false` once they went back on sale.
       */
      hold: z.object({ active: z.boolean(), seats: zCount, releaseBackAt: zEpochMs }).strict().optional(),
      /**
       * B549 (8): the GROUP PASS a CONFIRMED group-ticket booking carries —
       * its headcount so far and, where the stage signs tickets, the token
       * its QR encodes (the same bytes entitlement.token answers).
       */
      pass: z
        .object({ entitlementId: zId, seats: zCount, onBoard: zCount, remaining: zCount, token: z.string().min(1).optional() })
        .strict()
        .optional(),
      /** B549 (8): a per-seat booking's issued tickets; (8d) and how many were scanned (the switch shuts at the first). */
      tickets: z.object({ issued: zCount, onBoard: zCount }).strict().optional(),
      /**
       * B549 (7): the organizer's link for the SENT revision (RULED 2026-09-25 —
       * a signed capability token: this quote only, dies at `expiresAt`, revoked
       * by any new revision). The desk's page composes the hosted URL; absent
       * where nothing is sent, the booking is void, or the stage cannot sign.
       */
      organiserLink: z.object({ token: z.string().min(1), expiresAt: zEpochMs }).strict().optional(),
    })
    .strict(),
  exampleRequest: { inquiryId: "gi-1" },
  exampleResponse: {
    inquiry: {
      inquiryId: "gi-1",
      channelId: "web",
      organisation: "Lincoln Elementary",
      contactName: "Ana Ruiz",
      contactEmail: "ana@lincoln.example",
      headcount: 60,
      kind: "school",
      status: "NEW",
      createdAt: 1_789_900_000_000,
      revision: 1,
      quote: EXAMPLE_QUOTE,
    },
    pool: { capacity: 200, sold: 0, held: 6, heldByOtherQuotes: 6 },
  },
});

/**
 * B549 (4a) — SAVE DRAFT on 441:2. The desk names the selling channel, the
 * departure and pool, the ticketing mode, the party and the lines (what and
 * how many — the SERVER prices them off the channel's list), and the terms.
 * `revision` is the one the desk read: a stale one is a 409, never an
 * overwrite. The stage does not move; a revision of a sent quote is a draft
 * until re-sent, its seats still held.
 */
export const groupQuoteSave = writeOp({
  name: "group.quote.save",
  permission: "order:write",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: {
    inquiryId: zId,
    revision: zCount,
    channelId: zId,
    slotId: zId,
    poolId: zId,
    ticketing: z.enum(GROUP_TICKETING),
    headcount: z.number().int().min(1).max(100_000),
    chaperones: z.number().int().min(0).max(100_000),
    lines: z.array(zQuoteLineIn).min(1).max(20),
    terms: zTerms,
  },
  response: z.object({ inquiryId: zId, revision: zCount, quote: zQuote }).strict(),
  exampleRequest: {
    inquiryId: "gi-1",
    revision: 0,
    channelId: "groups",
    slotId: "harbor#2026-10-14T10:30",
    poolId: "pool-main",
    ticketing: "PER_SEAT",
    headcount: 54,
    chaperones: 6,
    lines: [{ productId: "harbor", variantId: "student", qty: 60 }],
    terms: { deposit: { kind: "PERCENT", percent: 25 }, balance: { kind: "DAYS_BEFORE_VISIT", days: 14 }, expiresAt: 1_791_000_000_000, collectVia: "HOSTED_INVOICE" },
  },
  exampleResponse: { inquiryId: "gi-1", revision: 1, quote: EXAMPLE_QUOTE },
});

/**
 * B549 (4a) — SEND QUOTE: the saved draft's seats are held as an allocation
 * carve-out on its pool until the quote expires (the release-back sweep
 * returns them if it lapses), and the inquiry becomes QUOTED. Not enough
 * seats = 409 and nothing moves. The quote email + hosted accept page are
 * their own leg (447:2); this door is the hold and the stage.
 */
export const groupQuoteSend = writeOp({
  name: "group.quote.send",
  permission: "order:write",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { inquiryId: zId, revision: zCount },
  response: z
    .object({ inquiryId: zId, revision: zCount, status: z.enum(GROUP_STAGES), allocationId: zId, heldSeats: z.number().int().min(1), expiresAt: zEpochMs })
    .strict(),
  exampleRequest: { inquiryId: "gi-1", revision: 1 },
  exampleResponse: { inquiryId: "gi-1", revision: 2, status: "QUOTED", allocationId: "gq-gi-1-r1", heldSeats: 60, expiresAt: 1_791_000_000_000 },
});

const zInvoiceOut = zInvoice;

/**
 * B549 (5) — MARK ACCEPTED on 443:2: the organizer said yes (by email or on
 * the phone — the hosted accept page is its own leg and will make this same
 * move). A SENT, unexpired quote whose seats are still held becomes
 * TENTATIVE and its invoice schedule is issued (deposit + balance, or one
 * full invoice). The seats stay held — until the later of the deposit's due
 * date and the option date while the deposit is unpaid, then to the visit.
 */
export const groupQuoteAccept = writeOp({
  name: "group.quote.accept",
  permission: "order:write",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { inquiryId: zId, revision: zCount },
  response: z.object({ inquiryId: zId, revision: zCount, status: z.enum(GROUP_STAGES), invoices: z.array(zInvoiceOut) }).strict(),
  exampleRequest: { inquiryId: "gi-1", revision: 2 },
  exampleResponse: {
    inquiryId: "gi-1",
    revision: 3,
    status: "TENTATIVE",
    invoices: [
      { number: "INV-HF7K-2Q9M-1", kind: "DEPOSIT", amountMinor: 27_750, currency: "USD", issuedAt: 1_790_100_000_000, dueAt: 1_790_700_000_000, afterVisit: false, payments: [], state: "OPEN", paidMinor: 0 },
      { number: "INV-HF7K-2Q9M-2", kind: "BALANCE", amountMinor: 83_250, currency: "USD", issuedAt: 1_790_100_000_000, dueAt: 1_790_790_400_000, afterVisit: false, payments: [], state: "OPEN", paidMinor: 0 },
    ],
  },
});

/**
 * B549 (5) — RECORD PAYMENT: money that arrived OFF-platform (bank transfer,
 * check, cash, a card taken elsewhere) recorded against one invoice, never
 * more than it still owes. A paid deposit commits the seats to the visit;
 * every pre-visit invoice paid confirms the booking. B549 (6, RULED
 * 2026-09-24 — deposit-as-liability): each payment posts a `deposit`
 * journal (settlement → deferred_revenue); at CONFIRMED the on_account order
 * is minted and one `tender` journal clears the liability.
 */
export const groupPaymentRecord = writeOp({
  name: "group.payment.record",
  permission: "order:write",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: {
    inquiryId: zId,
    revision: zCount,
    invoiceNumber: z.string().min(1).max(64),
    amountMinor: z.number().int().min(1).max(1_000_000_000),
    method: z.enum(GROUP_DESK_PAYMENT_METHODS),
    reference: z.string().min(1).max(120).optional(),
  },
  response: z.object({ inquiryId: zId, revision: zCount, status: z.enum(GROUP_STAGES), invoice: zInvoiceOut }).strict(),
  exampleRequest: { inquiryId: "gi-1", revision: 3, invoiceNumber: "INV-HF7K-2Q9M-1", amountMinor: 27_750, method: "BANK_TRANSFER", reference: "LINCOLN-DEP" },
  exampleResponse: {
    inquiryId: "gi-1",
    revision: 4,
    status: "TENTATIVE",
    invoice: {
      number: "INV-HF7K-2Q9M-1",
      kind: "DEPOSIT",
      amountMinor: 27_750,
      currency: "USD",
      issuedAt: 1_790_100_000_000,
      dueAt: 1_790_700_000_000,
      afterVisit: false,
      payments: [{ paymentId: "gp-1", amountMinor: 27_750, method: "BANK_TRANSFER", reference: "LINCOLN-DEP", receivedAt: 1_790_200_000_000, recordedAt: 1_790_200_000_000 }],
      state: "PAID",
      paidMinor: 27_750,
    },
  },
});

/**
 * B549 (5) — VOID & RELEASE SEATS: a quoted or tentative booking — the held
 * seats return to sale now, open invoices are voided, the stage is VOID.
 * B549 (6, RULED 2026-09-24): a booking with money recorded is voided too,
 * and its deposit journals are mirrored as refunds (the desk returns the
 * money the way it came).
 */
export const groupBookingVoid = writeOp({
  name: "group.booking.void",
  permission: "order:write",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { inquiryId: zId, revision: zCount },
  response: z.object({ inquiryId: zId, revision: zCount, status: z.enum(GROUP_STAGES), releasedSeats: zCount }).strict(),
  exampleRequest: { inquiryId: "gi-1", revision: 3 },
  exampleResponse: { inquiryId: "gi-1", revision: 4, status: "VOID", releasedSeats: 60 },
});

/**
 * B549 (8d) — the desk's TICKETING SWITCH (ruling (a), 2026-09-24: "the
 * choice is a field on the group booking, changeable until the first scan").
 * Before CONFIRMED only the field moves. On a CONFIRMED booking the old
 * tickets are voided (the gate's manifest carries them as revocations) and
 * the new shape is issued — refused once anybody was scanned. Asking for
 * the shape the booking already has re-walks the tickets and answers.
 */
export const groupTicketingSwitch = writeOp({
  name: "group.ticketing.switch",
  permission: "order:write",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { inquiryId: zId, revision: zCount, ticketing: z.enum(GROUP_TICKETING) },
  response: z.object({ inquiryId: zId, revision: zCount, ticketing: z.enum(GROUP_TICKETING), voided: zCount, issued: zCount }).strict(),
  exampleRequest: { inquiryId: "gi-1", revision: 5, ticketing: "PER_SEAT" },
  exampleResponse: { inquiryId: "gi-1", revision: 6, ticketing: "PER_SEAT", voided: 1, issued: 40 },
});

/**
 * B549 (6c) — Reports' OUTSTANDING GROUP BALANCES (RULED 2026-09-24: read from
 * the INVOICE records, not the ledger). Every TENTATIVE/CONFIRMED booking that
 * still owes, at the server's clock: invoiced, paid, outstanding, the overdue
 * part, and the next invoice to chase; ordered earliest-due first, with
 * per-currency totals. A money read, so it rides `ledger:read` like the rest
 * of Reports.
 */
const zBalanceNextDue = z
  .object({ number: z.string().min(1), kind: z.enum(GROUP_INVOICE_KINDS), dueAt: zEpochMs, outstandingMinor: zMinor, overdue: z.boolean() })
  .strict();

export const groupBalances = readOp({
  name: "group.balances",
  permission: "ledger:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: {},
  response: z
    .object({
      rows: z.array(
        z
          .object({
            inquiryId: zId,
            bookingRef: z.string().min(1).optional(),
            organisation: z.string().min(1),
            status: z.enum(["TENTATIVE", "CONFIRMED"]),
            visitAt: zEpochMs.optional(),
            currency: z.string().min(3).max(3),
            invoicedMinor: zMinor,
            paidMinor: zMinor,
            outstandingMinor: zMinor,
            overdueMinor: zMinor,
            nextDue: zBalanceNextDue.optional(),
          })
          .strict(),
      ),
      totals: z.array(z.object({ currency: z.string().min(3).max(3), outstandingMinor: zMinor, overdueMinor: zMinor, bookings: z.number().int().min(1) }).strict()),
    })
    .strict(),
  exampleRequest: {},
  exampleResponse: {
    rows: [
      {
        inquiryId: "gi-1",
        bookingRef: "HF7K-2Q9M",
        organisation: "Lakeview Elementary",
        status: "TENTATIVE",
        visitAt: 1_790_900_000_000,
        currency: "USD",
        invoicedMinor: 111_000,
        paidMinor: 10_000,
        outstandingMinor: 101_000,
        overdueMinor: 17_750,
        nextDue: { number: "INV-HF7K-2Q9M-1", kind: "DEPOSIT", dueAt: 1_790_700_000_000, outstandingMinor: 17_750, overdue: true },
      },
    ],
    totals: [{ currency: "USD", outstandingMinor: 101_000, overdueMinor: 17_750, bookings: 1 }],
  },
});

/**
 * B549 (8e) — TICKETS' DOWNLOAD PDF: the booking's CURRENT issue (the group
 * pass, or a page per guest ticket), rendered by us, each code the ticket's
 * own signed token. The desk hands it to the organizer until the mail
 * release sends it. Nothing issued yet is the uniform 404.
 */
export const groupTicketsDocument = readOp({
  name: "group.tickets.document",
  permission: "order:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { inquiryId: zId },
  response: z
    .object({
      fileName: z.string().min(1).max(80),
      contentType: z.literal("application/pdf"),
      bytes: z.number().int().min(1),
      pdfBase64: z.string().min(1),
    })
    .strict(),
  exampleRequest: { inquiryId: "gi-1" },
  exampleResponse: { fileName: "HF7K-2Q9M-group-pass.pdf", contentType: "application/pdf", bytes: 5, pdfBase64: "JVBERi0=" },
});

/**
 * B549 (6b) — the invoice's PDF, rendered by US from the invoice record
 * (RULED 2026-09-24, "our record, their rails": the PDF lives in the domain
 * and is provider-neutral — the same document whichever gateway collects).
 * The bytes ride the JSON door as base64 (an invoice is a few KB); the
 * record's number names the file. An invoice the booking does not carry is
 * the uniform 404 — a document about nothing is never drawn.
 */
export const groupInvoiceDocument = readOp({
  name: "group.invoice.document",
  permission: "order:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { inquiryId: zId, invoiceNumber: z.string().min(1).max(64) },
  response: z
    .object({
      fileName: z.string().min(1).max(80),
      contentType: z.literal("application/pdf"),
      bytes: z.number().int().min(1),
      pdfBase64: z.string().min(1),
    })
    .strict(),
  exampleRequest: { inquiryId: "gi-1", invoiceNumber: "INV-HF7K-2Q9M-1" },
  exampleResponse: { fileName: "INV-HF7K-2Q9M-1.pdf", contentType: "application/pdf", bytes: 5, pdfBase64: "JVBERi0=" },
});

/**
 * B549 (6b) — PAYMENT LINK: the provider's hosted invoice for exactly what one
 * group invoice still owes (RULED 2026-09-24: Stripe Invoices behind the
 * PaymentProviderPort — "our record, their rails"; the provider's invoice is
 * only a reference on ours). Raised on the tenant's connected account; the
 * organizer pays there, and the provider-signed `invoice.paid` records the
 * money. A second press answers the same live link. Off until the stage
 * switches online collection on (its webhook must be listening first).
 */
export const groupInvoiceCollect = writeOp({
  name: "group.invoice.collect",
  permission: "order:write",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { inquiryId: zId, revision: zCount, invoiceNumber: z.string().min(1).max(64) },
  response: z.object({ inquiryId: zId, revision: zCount, invoiceNumber: z.string().min(1), hostedUrl: z.string().url() }).strict(),
  exampleRequest: { inquiryId: "gi-1", revision: 3, invoiceNumber: "INV-HF7K-2Q9M-1" },
  exampleResponse: { inquiryId: "gi-1", revision: 4, invoiceNumber: "INV-HF7K-2Q9M-1", hostedUrl: "https://invoice.stripe.com/i/acct_1/test_1" },
});

/**
 * B549 (7) — the hosted accept-&-pay page (447:2), the organizer's side.
 *
 * RULED 2026-09-25 (Pathik): the organizer's emailed link carries a SIGNED
 * CAPABILITY TOKEN (gql1, domain `group-quote-link.ts`) — one per quote
 * revision, scope accept + pay THIS quote only, expiring at the option date,
 * revoked by any new revision; read by a PUBLIC door that answers only that
 * quote's face. No account, no email code.
 *
 * The token is the credential; the walls are: the signature (verified with
 * the PUBLIC half of the ticket custody — the door never signs), the tenant
 * the request claims must be the tenant the token names, the state read
 * against the ROW (a replaced, withdrawn or expired link answers only its
 * state), and the route throttle every public door carries. Anything the door
 * cannot verify is the uniform `{}` — no oracle for what exists.
 */
export const QUOTE_LINK_STATES = ["OPEN", "ACCEPTED", "PAID", "EXPIRED", "REPLACED", "WITHDRAWN"] as const;
export const QUOTE_PAY_BY = ["CARD", "BANK_TRANSFER"] as const;

const zQuoteLinkToken = z.string().min(1).max(2048);

const zQuotePageLine = z
  .object({ name: z.string().min(1), qty: z.number().int().min(1), groupUnitMinor: zMinor, publicUnitMinor: zMinor.optional(), lineMinor: zMinor })
  .strict();

const zQuotePageInstalment = z
  .object({
    kind: z.enum(GROUP_INVOICE_KINDS),
    /** The invoice number, once the acceptance issued it. */
    number: z.string().min(1).optional(),
    amountMinor: zMinor,
    dueAt: zEpochMs,
    afterVisit: z.boolean(),
    paidMinor: zMinor,
  })
  .strict();

/** The quote itself — only while the link is live (OPEN / ACCEPTED / PAID). Never the contact's email, the pool, or the desk's revision. */
const zQuotePageQuote = z
  .object({
    organisation: z.string().min(1),
    contactName: z.string().min(1),
    productNames: z.array(z.string().min(1)).min(1),
    visitAt: zEpochMs,
    headcount: zCount,
    chaperones: zCount,
    seats: zCount,
    currency: z.string().regex(/^[A-Z]{3}$/),
    lines: z.array(zQuotePageLine).min(1),
    totalMinor: zMinor,
    publicTotalMinor: zMinor.optional(),
    balance: zBalance,
    schedule: z.array(zQuotePageInstalment).min(1),
    /** What ACCEPT & PAY collects: the first installment still owed before the visit. Absent when nothing is. */
    payFirst: z.object({ kind: z.enum(GROUP_INVOICE_KINDS), amountMinor: zMinor, dueAt: zEpochMs, dueNow: z.boolean() }).strict().optional(),
    /** The stage collects online AND the quote's terms allow it — else the desk sends payment details. */
    payOnline: z.boolean(),
    collectVia: z.enum(GROUP_QUOTE_COLLECT),
    /** The booking's reference, once accepted. */
    bookingRef: z.string().min(1).optional(),
    /** B615 leg 3d — the show's rules before the organizer accepts (buyer voice; the visit's showtime age over the product's). */
    houseFacts: z.array(z.string().min(1).max(200)).max(20).optional(),
  })
  .strict();

const zQuotePage = z
  .object({
    state: z.enum(QUOTE_LINK_STATES),
    /** The business the organizer is dealing with (ops config's business name). */
    seller: z.string().min(1),
    /** The seller's zone — every date on the page is read in it. */
    timeZone: z.string().min(1),
    /** The option date the link dies at. */
    expiresAt: zEpochMs,
    quote: zQuotePageQuote.optional(),
  })
  .strict();

const EXAMPLE_PAGE = {
  state: "OPEN",
  seller: "Harbor Lights Cruises",
  timeZone: "America/Chicago",
  expiresAt: 1_790_600_000_000,
  quote: {
    organisation: "Lincoln Elementary",
    contactName: "Ms. Ortega",
    productNames: ["Sunset Cruise"],
    visitAt: 1_791_500_000_000,
    headcount: 58,
    chaperones: 2,
    seats: 60,
    currency: "USD",
    lines: [{ name: "Sunset Cruise · Student", qty: 58, groupUnitMinor: 1_950, publicUnitMinor: 2_400, lineMinor: 113_100 }],
    totalMinor: 113_100,
    publicTotalMinor: 139_200,
    balance: { kind: "DAYS_BEFORE_VISIT", days: 14 },
    schedule: [{ kind: "DEPOSIT", amountMinor: 28_275, dueAt: 1_790_000_000_000, afterVisit: false, paidMinor: 0 }],
    payFirst: { kind: "DEPOSIT", amountMinor: 28_275, dueAt: 1_790_000_000_000, dueNow: true },
    payOnline: true,
    collectVia: "PAY_PAGE",
  },
};

/** B549 (7) — OPEN THE LINK: the quote's face, the state of a dead link, or the uniform {}. */
export const groupQuoteOpen = publicOp({
  name: "group.quote.open",
  permission: "public",
  fields: { token: zQuoteLinkToken },
  response: z.object({ page: zQuotePage.optional() }).strict(),
  exampleRequest: { token: "gql1.eyJ0ZW5hbnRJZCI6InRlbmFudC0xIn0.k1.sig" },
  exampleResponse: { page: EXAMPLE_PAGE },
});

/**
 * B549 (7) — ACCEPT & PAY: accepts through the desk's own move (TENTATIVE +
 * the invoice schedule) and, paying by CARD where `payOnline`, answers the
 * provider's hosted page for what is due first (the same collect path the
 * desk's DOCUMENTS uses). Pressing again answers the same page — never a
 * second acceptance nor a second link. A dead link accepts nothing.
 */
export const groupQuoteRespond = publicOp({
  name: "group.quote.respond",
  permission: "public",
  fields: { token: zQuoteLinkToken, payBy: z.enum(QUOTE_PAY_BY) },
  response: z
    .object({
      page: zQuotePage.optional(),
      payUrl: z.string().url().optional(),
      /**
       * B549 (7c): the invoice's OWN payment, confirmed inline by the provider's
       * element on the page (447:2's card well) — the provider's client secret
       * for exactly this invoice, the tenant's connected account it lives on, and
       * the channel's publishable key. Absent → the hosted page (`payUrl`).
       */
      pay: z
        .object({
          provider: z.literal("stripe"),
          clientSecret: z.string().min(1),
          publishableKey: z.string().min(1),
          stripeAccount: z.string().min(1).optional(),
          amountMinor: zMinor,
          currency: z.string().regex(/^[A-Z]{3}$/),
        })
        .strict()
        .optional(),
    })
    .strict(),
  exampleRequest: { token: "gql1.eyJ0ZW5hbnRJZCI6InRlbmFudC0xIn0.k1.sig", payBy: "CARD" },
  exampleResponse: { page: { ...EXAMPLE_PAGE, state: "ACCEPTED", quote: { ...EXAMPLE_PAGE.quote, bookingRef: "HF7K-2Q9M" } }, payUrl: "https://invoice.stripe.com/i/acct_1/in_1" },
});

/**
 * B561 — the desk's four mails to the organizer (443:2's doors). Each sends
 * to the booking's OWN contact (never an address the caller types), as the
 * tenant ("<business> via Transact", replies to the tenant's contact email).
 * A send that lands is logged on the row (`mailed`, the revision moves); a
 * failed send logs nothing; a kind is capped at 3 an hour (429). A stage
 * with no mail sender refuses rather than pretending.
 */
const zMailSent = z
  .object({
    inquiryId: zId,
    revision: zCount,
    kind: z.enum(GROUP_MAIL_KINDS),
    sentTo: z.string().min(1),
    sentAt: zEpochMs,
    invoiceNumber: z.string().min(1).optional(),
  })
  .strict();

/** SEND QUOTE's mail: the organizer's signed `/q#` link for the SENT revision. Nothing sent = 409. */
export const groupQuoteEmail = writeOp({
  name: "group.quote.email",
  permission: "order:write",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { inquiryId: zId },
  response: zMailSent,
  exampleRequest: { inquiryId: "gi-1" },
  exampleResponse: { inquiryId: "gi-1", revision: 3, kind: "QUOTE", sentTo: "ana@lakeview.example", sentAt: 1_790_100_000_000 },
});

/** RESEND INVOICE: one invoice with its PDF attached (byte-equal with DOWNLOAD) and its live pay link, if any. */
export const groupInvoiceEmail = writeOp({
  name: "group.invoice.email",
  permission: "order:write",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { inquiryId: zId, invoiceNumber: z.string().min(1).max(64) },
  response: zMailSent,
  exampleRequest: { inquiryId: "gi-1", invoiceNumber: "INV-HF7K-2Q9M-1" },
  exampleResponse: { inquiryId: "gi-1", revision: 4, kind: "INVOICE", sentTo: "ana@lakeview.example", sentAt: 1_790_100_000_000, invoiceNumber: "INV-HF7K-2Q9M-1" },
});

/** RESEND PASS: the CURRENT issue's PDF (the group pass, or a page per guest). Nothing issued = 404. */
export const groupTicketsEmail = writeOp({
  name: "group.tickets.email",
  permission: "order:write",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { inquiryId: zId },
  response: zMailSent,
  exampleRequest: { inquiryId: "gi-1" },
  exampleResponse: { inquiryId: "gi-1", revision: 5, kind: "TICKETS", sentTo: "ana@lakeview.example", sentAt: 1_790_100_000_000 },
});

/**
 * SEND REMINDER: about the open quote (its link again) or, after acceptance,
 * the earliest-due invoice still owing (its PDF attached). Nothing to remind
 * of = 409 with the reason.
 */
export const groupReminderEmail = writeOp({
  name: "group.reminder.email",
  permission: "order:write",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { inquiryId: zId },
  response: zMailSent,
  exampleRequest: { inquiryId: "gi-1" },
  exampleResponse: { inquiryId: "gi-1", revision: 6, kind: "REMINDER", sentTo: "ana@lakeview.example", sentAt: 1_790_100_000_000, invoiceNumber: "INV-HF7K-2Q9M-1" },
});
